import { getAccessToken, refreshIfNeeded } from "@/lib/auth";

// "" en dev (proxy de Vite /api → :8000) y en producción (mismo origen).
const API_URL = import.meta.env.VITE_API_URL || "";

/** Error HTTP con status: permite a TanStack Query no reintentar los 4xx. */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Sesión muerta: borramos y mandamos al usuario al login. No usamos
 * `useNavigate` (estamos fuera de React) — un `window.location.assign`
 * fuerza recarga y deja el AuthProvider en estado limpio.
 */
function redirectToLogin() {
  // Evita bucle si ya estamos en /login.
  if (window.location.pathname !== "/login") {
    const callback = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.assign(`/login?callbackUrl=${callback}`);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await refreshIfNeeded();
  const effective = token ?? getAccessToken();
  return effective ? { Authorization: `Bearer ${effective}` } : {};
}

export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const isFormData = options.body instanceof FormData;

  // FormData: el navegador pone el Content-Type con su boundary; fijarlo
  // a mano rompe el multipart.
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
    ...(await authHeader()),
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error("Sesión expirada. Redirigiendo al login…");
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Error desconocido" }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export async function fetchBlobWithAuth(
  url: string,
  options: RequestInit = {}
) {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    ...(await authHeader()),
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error("Sesión expirada. Redirigiendo al login…");
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Error desconocido" }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status);
  }

  return response.blob();
}

// ---------------------------------------------------------------------------
// SSE sobre fetch: EventSource no permite POST ni cabecera Authorization,
// así que parseamos el stream a mano con ReadableStream.
// ---------------------------------------------------------------------------

export interface SSEEvent {
  event: string;
  data: unknown;
}

export async function streamWithAuth(
  url: string,
  options: RequestInit,
  onEvent: (e: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...(options.headers as Record<string, string>),
    ...(await authHeader()),
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
    signal,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error("Sesión expirada. Redirigiendo al login…");
  }

  if (!response.ok || !response.body) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Error desconocido" }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushBlock = (block: string) => {
    let event = "message";
    const dataLines: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }
    if (dataLines.length === 0) return;
    const raw = dataLines.join("\n");
    let data: unknown = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      /* data no-JSON: se entrega como string */
    }
    onEvent({ event, data });
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, "\n");
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        flushBlock(block);
      }
    }
    // Bloque final sin \n\n de cierre (algunos servidores no lo emiten).
    if (buffer.trim()) flushBlock(buffer);
  } finally {
    reader.releaseLock();
  }
}
